/**
 * The pens sequence. Ruling 59, 14 October 2026, and doc 03 section 6.1.
 *
 * The sequence doc 03 fixes is:
 *
 * 1. CONOPS intent.
 * 2. **General scheme of manoeuvre.** Phases named only. He is writing.
 * 3. **"Pens down, eyes up."** Detailed scheme, phase by phase, walked on the
 *    trace. Not writing.
 * 4. End of the last phase, into Main Effort and End State. Pens back up
 *    automatically, no verbal call.
 *
 * Step 3 is the part that did not exist. Doc 03 section 6.2: **the detailed
 * scheme is not in the document.** It lives in each sidecar phase's `detail`
 * field, so there is no node in the parse tree to deliver it at, and the
 * delivery schedule is built from the document alone. The narratives were
 * therefore never spoken, and `EYES_UP` had nothing to introduce.
 *
 * This module builds the missing part: an **interlude** that is spliced into
 * the schedule after the last CONOPS item a phase binds to. The interlude is
 * the call, then each phase's detail in phase order. It carries no doctrinal
 * content of its own beyond the locked call, and every narrative in it comes
 * from the sidecar the caller supplies.
 *
 * Ruling 59a says the cue fires **after** the last phase-bound item, not
 * before some item. That is why an interlude is needed rather than a
 * different binding: the node after the last phase name is the Main Effort,
 * and `PENS_RESUME` is bound to that. Binding `EYES_UP` there too would fire
 * both at once and the pens would go down and back up in the same breath.
 *
 * Behind the `eyesUp` flag, default off. With the flag off this returns an
 * empty sequence and a reason, and delivery is exactly what it was.
 */

import type { FeatureFlagService } from "../../../services/feature-flags";
import type { SpeechSegment } from "../../../services/speech";
import type { OrdersParseTree } from "../parser";
import type { DeliveryDisplayNode } from "./model";

/** Doc 03 section 6.1. Locked wording; this is the whole of it. */
export const PENS_DOWN_CALL = "Pens down, eyes up.";

/** The id suffix of the node that carries the call. */
export const PENS_DOWN_NODE = "pens-down";

/** A phase as the sequence needs it, which is what the sidecar carries. */
export interface PensSequencePhase {
  /** The CONOPS item this phase corresponds to in the general scheme. */
  readonly bindItemId: string;
  readonly detail: string;
  readonly id: string;
  readonly label: string;
}

export interface PensSequence {
  /** The node whose delivery fires `EYES_UP`, or null where there is none. */
  readonly callNodeId: string | null;
  /** Display nodes to splice in, in order. */
  readonly displayNodes: readonly DeliveryDisplayNode[];
  /** The node the interlude is spliced in after, or null. */
  readonly afterNodeId: string | null;
  /** Each interlude phase node, paired with the item its phase binds to. */
  readonly phaseNodes: readonly {
    readonly bindItemId: string;
    readonly nodeId: string;
    readonly phaseId: string;
  }[];
  /**
   * Why the sequence is empty, or why a phase was left out of it. Empty for a
   * sequence with nothing to say about itself.
   *
   * Reported rather than silent: a sequence that does not fire is the
   * difference between a delivery that walks the trace and one that does not,
   * and "nothing happened" is not a diagnosis.
   */
  readonly reasons: readonly string[];
  readonly segments: readonly SpeechSegment[];
}

const EMPTY: PensSequence = Object.freeze({
  afterNodeId: null,
  callNodeId: null,
  displayNodes: [],
  phaseNodes: [],
  reasons: [],
  segments: [],
});

function empty(...reasons: readonly string[]): PensSequence {
  return { ...EMPTY, reasons };
}

/** Every CONOPS item id, in delivery order. */
function conopsItemIds(tree: OrdersParseTree): readonly string[] {
  for (const paragraph of tree.paragraphs) {
    for (const subparagraph of paragraph.subparagraphs) {
      if (subparagraph.id.endsWith("/CONOPS")) {
        return subparagraph.items.map(({ id }) => id);
      }
    }
  }
  return [];
}

/**
 * The interlude for one document and one set of phases.
 *
 * The phases are spoken in **phase order** — the order the sidecar lists them
 * — and not in the order their bound items appear. Ruling 59b says "in phase
 * order", and a sidecar is at liberty to bind phase two to an earlier item
 * than phase one without that reordering the operation.
 */
export function pensSequenceFor(
  tree: OrdersParseTree,
  phases: readonly PensSequencePhase[],
  flags: FeatureFlagService,
): PensSequence {
  if (!flags.isEnabled("eyesUp")) {
    return empty("the eyesUp flag is off");
  }
  if (phases.length === 0) {
    return empty("the trace carries no phases");
  }
  const itemIds = conopsItemIds(tree);
  if (itemIds.length === 0) {
    return empty("this document has no concept of operations subparagraph");
  }

  // Ruling 59a: after the LAST CONOPS item a phase binds to. "Last" is the
  // document's own order, not the sidecar's.
  const bound = phases
    .map(({ bindItemId }) => itemIds.indexOf(bindItemId))
    .filter((index) => index >= 0);
  if (bound.length === 0) {
    return empty("no phase binds to an item in this document");
  }
  const afterNodeId = itemIds[Math.max(...bound)];
  /* v8 ignore next -- the index came from indexOf on this same list */
  if (afterNodeId === undefined) return empty("the bound item vanished");

  const base = afterNodeId.slice(0, afterNodeId.lastIndexOf("/"));
  const callNodeId = `${base}/${PENS_DOWN_NODE}`;
  const displayNodes: DeliveryDisplayNode[] = [
    { id: callNodeId, kind: "item", text: PENS_DOWN_CALL },
  ];
  const segments: SpeechSegment[] = [
    {
      id: `${callNodeId}/speech-01`,
      kind: "item",
      text: PENS_DOWN_CALL,
      // The call is an instruction he must act on, not body text.
      zone: "critical",
    },
  ];
  const phaseNodes: {
    bindItemId: string;
    nodeId: string;
    phaseId: string;
  }[] = [];
  const reasons: string[] = [];

  for (const phase of phases) {
    const detail = phase.detail.trim();
    if (detail === "") {
      reasons.push(`phase ${phase.id} carries no detail, so nothing is spoken`);
      continue;
    }
    if (!itemIds.includes(phase.bindItemId)) {
      reasons.push(
        `phase ${phase.id} binds to an item this document does not have`,
      );
      continue;
    }
    const nodeId = `${base}/${phase.id}`;
    displayNodes.push({ id: nodeId, kind: "item", text: phase.label });
    segments.push({
      id: `${nodeId}/speech-01`,
      kind: "item",
      text: detail,
      zone: "body",
    });
    phaseNodes.push({
      bindItemId: phase.bindItemId,
      nodeId,
      phaseId: phase.id,
    });
  }

  if (phaseNodes.length === 0) {
    return empty(...reasons, "no phase had anything to speak");
  }
  return {
    afterNodeId,
    callNodeId,
    displayNodes,
    phaseNodes,
    reasons,
    segments,
  };
}

/**
 * `list` with `insert` placed immediately after the entry whose node id is
 * `afterNodeId`.
 *
 * Both the display nodes and the speech segments need the same splice, and
 * they identify their entries differently — a segment id carries a
 * `/speech-NN` suffix and a display node does not — so the caller supplies
 * the accessor. The insert goes after the **last** matching entry, because a
 * long item is several chunked segments and the interlude follows all of them.
 */
export function spliceAfterNode<T>(
  list: readonly T[],
  insert: readonly T[],
  afterNodeId: string | null,
  nodeIdOf: (entry: T) => string,
): readonly T[] {
  if (afterNodeId === null || insert.length === 0) return list;
  let at = -1;
  for (const [index, entry] of list.entries()) {
    if (nodeIdOf(entry) === afterNodeId) at = index;
  }
  // A sequence built from a different document than the one being delivered
  // appends nothing rather than landing the interlude in the wrong place.
  if (at < 0) return list;
  return [...list.slice(0, at + 1), ...insert, ...list.slice(at + 1)];
}

/**
 * The item the trace pane should treat as current, for a delivery node.
 *
 * Two translations, and both matter:
 *
 * - An **interlude phase node** is reported as the item its phase binds to,
 *   so `phasesBoundTo` finds it and the walk runs while the detail is spoken.
 * - A **bound item itself** is reported as nothing. That item is a phase name
 *   in the general scheme, delivered while he is still writing, and walking
 *   the trace under his pen is the opposite of what doc 03 section 6.1 asks
 *   for. Before this, the walk would have run there and nowhere else.
 *
 * Every other node passes through unchanged, so a delivery with no sequence
 * behaves exactly as it did.
 */
export function traceItemForNode(
  sequence: PensSequence,
  nodeId: string | null,
): string | null {
  if (nodeId === null) return null;
  const interlude = sequence.phaseNodes.find((node) => node.nodeId === nodeId);
  if (interlude !== undefined) return interlude.bindItemId;
  const isBoundItem = sequence.phaseNodes.some(
    (node) => node.bindItemId === nodeId,
  );
  return isBoundItem ? null : nodeId;
}
